import {Color, Scene, TextureLoader, Vector2} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'

import copilotHeadGlb from '../../assets/copilotHead/copilot_head.glb'
import earSrc from '../../assets/copilotHead/ear.webp'
import eyesSrc from '../../assets/copilotHead/eyes.webp'
import glassSrc from '../../assets/copilotHead/glass.webp'
import goggleSrc from '../../assets/copilotHead/goggle.webp'
import headSrc from '../../assets/copilotHead/head.webp'
import matcapSrc from '../../assets/copilotHead/matcap.webp'
import screenSrc from '../../assets/copilotHead/screen.webp'
import ventoSrc from '../../assets/copilotHead/vento.webp'
import type {Diffuses, Gltfs, Images} from './assetTypes'

export const diffuses: Diffuses = {
  Ears: {
    src: earSrc,
    texture: null,
    flipY: false,
    isFace: true,
    isGoggle: false,
    fresnelIntensity: 0.6,
    fresnelColor: new Color(0xa9fbff),
    fresnelPosRange: new Vector2(2.0, -1.0),
    matcapIntensity: 1,
    specularIntensity: 1,
  },
  Eyes: {
    src: eyesSrc,
    texture: null,
    flipY: false,
    isFace: true,
    isGoggle: false,
    fresnelIntensity: 0,
    fresnelColor: new Color(0xbf70ff),
    fresnelPosRange: new Vector2(2.0, -1.0),
    matcapIntensity: 0,
    specularIntensity: 0,
  },
  Glass: {
    src: glassSrc,
    texture: null,
    flipY: false,
    isFace: false,
    isGoggle: true,
    fresnelIntensity: 1,
    fresnelColor: new Color(0x5332b3),
    fresnelPosRange: new Vector2(0.0, 1.0),
    matcapIntensity: 0.5,
    specularIntensity: 0,
  },
  Goggle: {
    src: goggleSrc,
    texture: null,
    flipY: false,
    isFace: false,
    isGoggle: true,
    fresnelIntensity: 0.5,
    fresnelColor: new Color(0x91deff),
    fresnelPosRange: new Vector2(0.0, 1.0),
    matcapIntensity: 2,
    specularIntensity: 0,
  },
  Head: {
    src: headSrc,
    texture: null,
    flipY: false,
    isFace: true,
    isGoggle: false,
    fresnelIntensity: 0.6,
    fresnelColor: new Color(0xa9fbff),
    fresnelPosRange: new Vector2(2.0, -2.0),
    matcapIntensity: 1,
    specularIntensity: 1,
  },
  Screen: {
    src: screenSrc,
    texture: null,
    flipY: false,
    isFace: true,
    isGoggle: false,
    fresnelIntensity: 1,
    fresnelColor: new Color(0x110f96),
    fresnelPosRange: new Vector2(2.0, -1.0),
    matcapIntensity: 0,
    specularIntensity: 1,
  },
  Vents: {
    src: ventoSrc,
    texture: null,
    flipY: false,
    isFace: true,
    isGoggle: false,
    fresnelIntensity: 0,
    fresnelColor: new Color(0xbf70ff),
    fresnelPosRange: new Vector2(2.0, -1.0),
    matcapIntensity: 1,
    specularIntensity: 1,
  },
}

export const images: Images = {
  matcap: {
    src: matcapSrc,
    texture: null,
    flipY: true,
  },
}

export const gltfs: Gltfs = {
  head: {
    src: copilotHeadGlb,
    scene: new Scene(),
  },
}

const loadGltfs = () => {
  const loader = new GLTFLoader()

  return Object.values(gltfs).map(
    gltf =>
      new Promise((resolve, reject) => {
        loader.load(
          gltf.src,
          loadedGltf => {
            gltf.scene = loadedGltf.scene
            resolve(loadedGltf.scene)
          },
          undefined,
          error => reject(error instanceof Error ? error : new Error('Asset load failed')),
        )
      }),
  )
}

const loadImages = () => {
  const loader = new TextureLoader()

  return Object.values(images).map(
    image =>
      new Promise((resolve, reject) => {
        loader.load(
          image.src,
          texture => {
            image.texture = texture
            texture.flipY = image.flipY
            resolve(texture)
          },
          undefined,
          error => reject(error instanceof Error ? error : new Error('Asset load failed')),
        )
      }),
  )
}

const loadDiffuses = () => {
  const loader = new TextureLoader()

  return Object.values(diffuses).map(
    diffuse =>
      new Promise((resolve, reject) => {
        loader.load(
          diffuse.src,
          texture => {
            diffuse.texture = texture
            texture.flipY = diffuse.flipY
            resolve(texture)
          },
          undefined,
          error => reject(error instanceof Error ? error : new Error('Asset load failed')),
        )
      }),
  )
}

export const load = (callback: () => void) => {
  const allPromises = [...loadImages(), ...loadGltfs(), ...loadDiffuses()]

  Promise.all(allPromises).then(() => {
    if (callback) callback()
  })
}
